/**
 * LLM 실행 전 payload 미리보기 + 실행/중지 모달.
 *
 * 상태:
 *  - idle: 미리보기 표시 + [실행] / [취소]
 *  - running: 실행 중 spinner + [중지]
 *  - (완료/에러는 부모가 모달 닫아서 처리)
 */

import { useEffect, useState } from 'react'
import { Modal } from '../../ui/Modal'
import { Button } from '../../ui/Button'
import { Spinner } from '../../ui/Spinner'
import type { LlmPayloadPreview } from './useStepwiseImport'

interface Props {
  open: boolean
  preview: LlmPayloadPreview | null
  running: boolean
  /** 사용자가 편집한 system prompt 를 인자로 전달 (편집 없었으면 원본과 동일). */
  onConfirm: (editedSystem: string) => void
  onCancel: () => void
  onStop: () => void
  /** 선택된 LLM 모델 id (undefined = 서버 기본값). preview.model 과는 별개 — 사용자가 방금 고른 값. */
  llmModel?: string
  /** 모델 변경 시 호출. 부모가 hook.setLlmModel + previewLlmPayload 재호출로 payload/maxTokens 갱신. */
  onModelChange: (model: string) => void
  textModels: Array<{ id: string; displayName: string }>
}

export function LlmPayloadModal({ open, preview, running, onConfirm, onCancel, onStop, llmModel, onModelChange, textModels }: Props) {
  const [showUser, setShowUser] = useState(true)
  // system prompt 편집용 로컬 state. preview.system 이 바뀌면 초기화.
  const [systemDraft, setSystemDraft] = useState('')
  useEffect(() => {
    if (preview) setSystemDraft(preview.system)
  }, [preview])
  const isSystemEdited = preview !== null && systemDraft !== preview.system

  return (
    <Modal
      open={open}
      onClose={running ? () => undefined : onCancel}
      title="LLM 호출 payload 확인"
      size="lg"
    >
      {!preview ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spinner /> 미리보기 로드 중...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '70vh' }}>
          {/* 요약 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 8,
              padding: 10,
              background: '#fafafa',
              border: '1px solid #e5e5e5',
              borderRadius: 6,
              fontSize: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>모델</div>
              <select
                value={llmModel ?? ''}
                onChange={(e) => onModelChange(e.target.value)}
                disabled={running || textModels.length === 0}
                style={{ marginTop: 2, padding: '3px 6px', fontSize: 12, fontWeight: 500, borderRadius: 4, border: '1px solid #ccc', width: '100%' }}
              >
                {textModels.length === 0 && <option value="">(기본)</option>}
                {textModels.map((m) => (
                  <option key={m.id} value={m.id}>{m.displayName}</option>
                ))}
              </select>
            </div>
            <Metric label="max tokens" value={String(preview.maxTokens)} />
            <Metric label="원본 HTML" value={`${preview.originalHtmlLength.toLocaleString()}자`} />
            <Metric
              label="마스킹 후"
              value={`${preview.maskedHtmlLength.toLocaleString()}자 · script ${preview.maskedScriptCount}/style ${preview.maskedStyleCount}`}
              highlight
            />
          </div>

          {/* system prompt — 편집 가능 textarea */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                background: '#f0f0f0',
                border: '1px solid #e5e5e5',
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <span>
                system prompt ({systemDraft.length.toLocaleString()}자)
                {isSystemEdited && <span style={{ marginLeft: 8, color: '#d48806', fontWeight: 400 }}>· 편집됨</span>}
              </span>
              {isSystemEdited && !running && (
                <button
                  type="button"
                  onClick={() => setSystemDraft(preview.system)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#0070c0',
                    cursor: 'pointer',
                    fontSize: 12,
                    padding: 0,
                  }}
                >
                  원본으로 복원
                </button>
              )}
            </div>
            <textarea
              value={systemDraft}
              onChange={(e) => setSystemDraft(e.target.value)}
              disabled={running}
              spellCheck={false}
              style={{
                ...codeBlockStyle,
                resize: 'vertical',
                height: 280,
                border: `1px solid ${isSystemEdited ? '#d48806' : '#333'}`,
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* user message */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
            <button
              onClick={() => setShowUser((v) => !v)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                background: '#f0f0f0',
                border: '1px solid #e5e5e5',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {showUser ? '▾' : '▸'} user message ({preview.userMessage.length.toLocaleString()}자)
            </button>
            {showUser && (
              <pre style={{ ...codeBlockStyle, flex: 1, minHeight: 200, overflow: 'auto' }}>{preview.userMessage}</pre>
            )}
          </div>

          {/* 액션 */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e5e5', paddingTop: 12 }}>
            {running ? (
              <>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, color: '#1890ff', fontSize: 13 }}>
                  <Spinner size="sm" /> Claude 응답 대기 중...
                </div>
                <Button variant="outline" onClick={onStop}>중지</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={onCancel}>취소</Button>
                <Button variant="primary" onClick={() => onConfirm(systemDraft)}>이 payload 로 실행</Button>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

function Metric({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, color: highlight ? '#0070c0' : '#333', marginTop: 2 }}>{value}</div>
    </div>
  )
}

const codeBlockStyle: React.CSSProperties = {
  margin: 0,
  marginTop: 4,
  padding: 12,
  background: '#1e1e1e',
  color: '#d4d4d4',
  fontSize: 12,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  borderRadius: 4,
  maxHeight: 300,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}
